import { BoosterId } from 'src/game/minigame/match2-timestep/defs/booster';
import { Match2PowerupType } from '../match2.ruleset';
import { appInstallReward } from '../../native/native.ruleset';
import { duration } from '../../utils/duration';
import { ChestType } from 'src/game/minigame/match2-timestep/rewards/match2-rewards.utils';

import {
  DecorationThemeID,
  DecorationThemeVariant,
} from 'src/replicant/data/DecorationItems.schema';
import { CardPackTier } from 'src/replicant/cardpack/cardpack.ruleset';
import type { CardId } from 'src/replicant/data/cards/CardCollections.schema';

export interface RandomRewardConfig {
  min?: number;
  max: number;
  step?: number;
  floor?: boolean;
}

export const match2Multipliers = <const>['frenzy'];
export type MultiplierType = (typeof match2Multipliers)[number];
export type Match2RewardType = Match2Reward['type'];
export const match2TimedRewardTypes = [
  'energy',
  'boosters-dart',
  'boosters-bullet',
  'boosters-drill',
  'boosters-roulette',
  'powerups-rocket',
  'powerups-bomb',
  'powerups-cube',
  'multiplier-frenzy',
] as const;
export type Match2TimedRewardType = (typeof match2TimedRewardTypes)[number];
export type RewardDecorationType = RewardDecoration['type'];

/** All Match2 reward types */
export type Match2Reward =
  | BPointsReward
  | GemsReward
  | EnergyReward
  | BoostersReward
  | PowerupsReward
  | MovesReward
  | MultiplierReward
  | FeatureReward
  | RewardSet
  | RewardWrapper
  | RoomDecorationReward
  | CardPackReward
  | CardReward;

/** All reward types with sub type */
export type RewardWithSubType =
  | FixedBoostersReward
  | TimedBoostersReward
  | FixedPowerupsReward
  | TimedPowerupsReward
  | MultiplierReward
  | FeatureReward;

/** All random reward types */
export type RandomReward =
  | RandomizedBPointsReward
  | RandomizedGemsReward
  | RandomizedFixedBoostersReward
  | RandomizedTimedBoostersReward
  | RandomizedFixedPowerupsReward
  | RandomizedTimedPowerupsReward
  | RandomizedRewardSet;

/** All fixed reward types */
export type FixedReward =
  | FixedBPointsReward
  | FixedGemsReward
  | FixedBoostersReward
  | FixedPowerupsReward
  | MovesReward
  | FeatureReward;

/** All timed reward types */
export type TimedReward =
  | TimedEnergyReward
  | TimedBoostersReward
  | TimedPowerupsReward
  | MultiplierReward;

/** All nested reward types */
export type NestedReward = RewardSet | RewardWrapper;

/** All reward types that require processing */
export type UnprocessedReward = RandomReward | NestedReward;

export const isRewardWithSubType = (
  r: Match2Reward,
): r is RewardWithSubType => {
  return 'subType' in r;
};

export const isRandomReward = (r: Match2Reward): r is RandomReward => {
  return 'randomize' in r;
};
export const isTimedReward = (r: Match2Reward): r is TimedReward => {
  return 'duration' in r;
};

export const isNestedReward = (r: Match2Reward): r is NestedReward => {
  return r.type === 'reward_set' || r.type === 'wrapper';
};

export const isUnprocessedReward = (
  r: Match2Reward,
): r is UnprocessedReward => {
  return isRandomReward(r) || isNestedReward(r);
};

// ---------------------- B-POINTS -------------------------

export type BPointsReward = FixedBPointsReward | RandomizedBPointsReward;

export interface FixedBPointsReward {
  type: 'bPoints';
  amount: number;
}

export interface RandomizedBPointsReward extends RandomRewardConfig {
  type: 'bPoints';
  randomize: true;
}

// ----------------------- GEMS ----------------------------

export type GemsReward = FixedGemsReward | RandomizedGemsReward;

export interface FixedGemsReward {
  type: 'gems';
  amount: number;
}

export interface RandomizedGemsReward extends RandomRewardConfig {
  type: 'gems';
  randomize: true;
}

// ----------------------- ENERGY --------------------------

export type EnergyReward = FixedEnergyReward | TimedEnergyReward;

export interface FixedEnergyReward {
  type: 'energy';
  amount: number;
}

export interface TimedEnergyReward {
  type: 'energy';
  duration: number;
}

// ---------------------- BOOSTERS -------------------------

export type BoostersReward =
  | FixedBoostersReward
  | TimedBoostersReward
  | RandomizedFixedBoostersReward
  | RandomizedTimedBoostersReward;

export interface FixedBoostersReward {
  type: 'boosters';
  subType: BoosterId;
  amount: number;
}

export interface TimedBoostersReward {
  type: 'boosters';
  subType: BoosterId;
  duration: number;
}

export interface RandomizedFixedBoostersReward {
  type: 'boosters';
  randomize: true;
  amount: number;
}

export interface RandomizedTimedBoostersReward {
  type: 'boosters';
  randomize: true;
  duration: number;
}

// ---------------------- POWERUPS -------------------------

export type PowerupsReward =
  | FixedPowerupsReward
  | TimedPowerupsReward
  | RandomizedFixedPowerupsReward
  | RandomizedTimedPowerupsReward;

export interface FixedPowerupsReward {
  type: 'powerups';
  subType: Match2PowerupType;
  amount: number;
}

export interface TimedPowerupsReward {
  type: 'powerups';
  subType: Match2PowerupType;
  duration: number;
}

export interface RandomizedFixedPowerupsReward {
  type: 'powerups';
  randomize: true;
  amount: number;
}

export interface RandomizedTimedPowerupsReward {
  type: 'powerups';
  randomize: true;
  duration: number;
}

// ----------------- MOVES / CONTINUES ---------------------

export interface MovesReward {
  type: 'moves';
  amount: number;
}

// -------------------- MULTIPLIERS ------------------------

export interface MultiplierReward {
  type: 'multiplier';
  subType: MultiplierType;
  amount: number;
  duration: number;
}

// ---------------------- FEATURE --------------------------

export type FeatureReward =
  | FrenzyObjective
  | RacePointsReward
  | TeamEventReward
  | SuperLollipopReward;

export interface FrenzyObjective {
  type: 'feature';
  subType: 'frenzy_objective';
  amount: number;
}

export interface RacePointsReward {
  type: 'feature';
  subType: 'race_points';
  raceId: string;
  amount: number;
}

export interface TeamEventReward {
  type: 'feature';
  subType: 'team_event' | 'team_competition_event' | 'team_chest_event'; // TODO: remove team_event after some time
  amount: number;
}

export interface SuperLollipopReward {
  type: 'feature';
  subType: 'superLollipop';
}

// -------------------- REWARD SET ------------------------

export type RewardSet = FixedRewardSet | RandomizedRewardSet;

export interface FixedRewardSet {
  type: 'reward_set';
  rewards: Match2Reward[];
  decoration?: RewardDecoration;
}

export interface RandomizedRewardSet {
  type: 'reward_set';
  randomize: true;
  pool: Match2Reward[];
  /** number of items to be picked from the pool, capped at its size */
  amount?: number;
  /** specify whether the picks should be mutually exclusive */
  noOverlap?: boolean;
  decoration?: RewardDecoration;
}

// ------------------- WRAPPERS ------------------------

/**
 * Wrappers are used to refer to a predefined set of rewards or to group them together,
 * as well as to add decoration to them.
 */
export type RewardWrapper = MultipleRewards | CombinedRewards;

export interface MultipleRewards {
  type: 'wrapper';
  id: PredefinedRewardId;
  amount: number;
  decoration?: RewardDecoration;
}

export interface CombinedRewards {
  type: 'wrapper';
  ids: PredefinedRewardId[];
  decoration?: RewardDecoration;
}

// ------------------- DECORATIONS ----------------------

/**
 * Decorations are used to describe how they should behave
 * on the UX side. ie. 'Chest' reward should trigger a chest
 * popup while 'Bundle' reward should use its ID to display
 * an accompanying icon.
 */
export type RewardDecoration = ChestDecoration | BundleDecoration;

export interface ChestDecoration {
  type: 'chest';
  subType: ChestType;
}

export interface BundleDecoration {
  type: 'bundle';
  id?: PredefinedRewardId;
}

// ------------------- ROOM DECORATION ----------------------

export interface RoomDecorationReward {
  type: 'roomDecoration';
  theme: DecorationThemeID;
  color: DecorationThemeVariant;
  tier: 1 | 2 | 3;
}

// ------------------- CARD PACK ----------------------

export interface CardPackReward {
  type: 'cardPack';
  tier: CardPackTier;
}

// ------------------- CARD ----------------------

export interface CardReward {
  type: 'card';
  cardId: CardId;
}

// ---------------- PREDEFINED REWARDS ------------------

export type PredefinedRewardId =
  | 'all_powerups'
  | 'all_boosters'
  | 'lollipop_firecracker'
  | 'firecracker_balloon'
  | 'nativeInstallRewards'
  | 'dailyBonusReward'
  | 'firstDailyBonusReward'
  | 'dailyBonusRewardNoBPoints'
  | 'firstDailyBonusRewardNoBPoints'
  | 'dailyBonusRewardNoBPointsSingle'
  | FrenzyRewardId;

export type FrenzyRewardId =
  | 'frenzy_chest_1'
  | 'frenzy_chest_2'
  | 'frenzy_chest_3';

export const match2PredefinedRewards: Record<
  PredefinedRewardId,
  Match2Reward[]
> = {
  // Bundles
  all_powerups: [
    { type: 'powerups', subType: 'rocket', amount: 1 },
    { type: 'powerups', subType: 'bomb', amount: 1 },
    { type: 'powerups', subType: 'cube', amount: 1 },
  ],
  all_boosters: [
    { type: 'boosters', subType: 'dart', amount: 1 },
    { type: 'boosters', subType: 'bullet', amount: 1 },
    { type: 'boosters', subType: 'drill', amount: 1 },
    { type: 'boosters', subType: 'roulette', amount: 1 },
  ],
  lollipop_firecracker: [
    { type: 'powerups', subType: 'cube', amount: 1 },
    { type: 'powerups', subType: 'rocket', amount: 1 },
  ],
  firecracker_balloon: [
    { type: 'powerups', subType: 'rocket', amount: 1 },
    { type: 'powerups', subType: 'bomb', amount: 1 },
  ],

  // Chests
  frenzy_chest_1: [
    { type: 'powerups', subType: 'rocket', amount: 1 },
    { type: 'boosters', subType: 'drill', amount: 1 },
    { type: 'boosters', subType: 'bullet', amount: 1 },
  ],
  frenzy_chest_2: [
    { type: 'powerups', subType: 'bomb', amount: 1 },
    { type: 'boosters', subType: 'roulette', amount: 1 },
  ],
  frenzy_chest_3: [
    { type: 'boosters', subType: 'roulette', amount: 1 },
    { type: 'boosters', subType: 'drill', amount: 1 },
    { type: 'boosters', subType: 'bullet', amount: 1 },
  ],

  // Misc
  nativeInstallRewards: [
    {
      type: 'wrapper',
      id: 'all_powerups',
      amount: 5,
    },
    { type: 'gems', amount: appInstallReward.gems },
  ],

  dailyBonusReward: [
    {
      type: 'bPoints',
      randomize: true,
      min: 1500,
      max: 2000,
      step: 100,
      floor: true,
    },
  ],
  firstDailyBonusReward: [
    {
      type: 'wrapper',
      id: 'dailyBonusReward',
      amount: 1,
    },
    {
      type: 'reward_set',
      randomize: true,
      pool: [
        { type: 'gems', amount: 100 },
        { type: 'powerups', subType: 'rocket', amount: 1 },
        { type: 'powerups', subType: 'bomb', amount: 1 },
        { type: 'powerups', subType: 'cube', amount: 1 },
        { type: 'boosters', subType: 'dart', amount: 1 },
        { type: 'boosters', subType: 'drill', amount: 1 },
        { type: 'boosters', subType: 'bullet', amount: 1 },
        { type: 'boosters', subType: 'roulette', amount: 1 },
      ],
    },
  ],
  dailyBonusRewardNoBPoints: [
    {
      type: 'reward_set',
      randomize: true,
      pool: [
        { type: 'powerups', subType: 'rocket', amount: 1 },
        { type: 'powerups', subType: 'bomb', amount: 1 },
        { type: 'powerups', subType: 'cube', amount: 1 },
        { type: 'boosters', subType: 'dart', amount: 1 },
        { type: 'boosters', subType: 'drill', amount: 1 },
        { type: 'boosters', subType: 'bullet', amount: 1 },
        { type: 'boosters', subType: 'roulette', amount: 1 },
      ],
    },
  ],
  firstDailyBonusRewardNoBPoints: [
    {
      type: 'wrapper',
      id: 'dailyBonusRewardNoBPoints',
      amount: 1,
    },
    {
      type: 'reward_set',
      randomize: true,
      pool: [
        { type: 'gems', amount: 100 },
        { type: 'powerups', subType: 'rocket', amount: 1 },
        { type: 'powerups', subType: 'bomb', amount: 1 },
        { type: 'powerups', subType: 'cube', amount: 1 },
        { type: 'boosters', subType: 'dart', amount: 1 },
        { type: 'boosters', subType: 'drill', amount: 1 },
        { type: 'boosters', subType: 'bullet', amount: 1 },
        { type: 'boosters', subType: 'roulette', amount: 1 },
      ],
    },
  ],
  dailyBonusRewardNoBPointsSingle: [
    {
      type: 'reward_set',
      randomize: true,
      pool: [
        { type: 'gems', amount: 100 },
        { type: 'powerups', subType: 'rocket', amount: 1 },
        { type: 'powerups', subType: 'bomb', amount: 1 },
        { type: 'powerups', subType: 'cube', amount: 1 },
        { type: 'boosters', subType: 'dart', amount: 1 },
        { type: 'boosters', subType: 'drill', amount: 1 },
        { type: 'boosters', subType: 'bullet', amount: 1 },
        { type: 'boosters', subType: 'roulette', amount: 1 },
      ],
    },
  ],
};
